"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, FileText, Layers, ShieldCheck, SlidersHorizontal, Truck, Wind } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const costFactors = [{
  icon: Layers,
  title: "Bull & Motion System",
  detail: "The powered base and drive mechanism of the machine."
}, {
  icon: SlidersHorizontal,
  title: "Controller",
  detail: "The operator console supplied with the exact machine."
}, {
  icon: ShieldCheck,
  title: "Inflatable Safety Arena",
  detail: "The inflatable landing area surrounding the bull, when included."
}, {
  icon: Wind,
  title: "Blower",
  detail: "The air supply unit for the inflatable, when included."
}, {
  icon: CheckCircle2,
  title: "Included Accessories",
  detail: "Cables, remotes, covers or other supplied items."
}, {
  icon: Truck,
  title: "Delivery Requirements",
  detail: "Packing and delivery arrangements for the exact order."
}];
export default function CostOverview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  return <section id="mechanicalbullcostoverview" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={fadeIn} className="mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl">
            How Much Does an Inflatable Mechanical Bull Cost?
          </h2>

          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            The cost of an inflatable mechanical bull depends on the exact machine, whether it is new or used, the controller, inflatable safety arena, blower, included accessories, condition and delivery requirements.
          </p>
        </motion.div>

        {/* Advisory Card / Package Comparison Rule */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={fadeIn} className="mt-12">
          <Card className="border border-border bg-card shadow-xs">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col gap-6 md:flex-row md:items-start">
                <div className="flex size-12 shrink-0 items-center justify-center border border-border bg-muted text-primary">
                  <AlertCircle className="size-6" />
                </div>
                <div className="space-y-4">
                  <h3 className="font-serif text-xl font-bold uppercase tracking-wide text-foreground">
                    Package Inclusions Dictate Total Investment
                  </h3>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    Do not compare two headline prices until you know what each package includes. One listing may include the complete bull, controller, inflatable and blower, while another may require additional equipment before it can be used as intended.
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    Current product prices provide a more useful comparison than a generic market average.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Key Component Breakdown Grid */}
        <div className="mt-12">
          <div className="mb-6 flex flex-col justify-between gap-2 border-b border-border pb-4 sm:flex-row sm:items-end">
            <div>
              <h3 className="font-serif text-2xl font-bold uppercase text-foreground">
                What Determines Your Exact Quotation
              </h3>
            </div>
            <span className="text-xs text-muted-foreground">
              6 Core Equipment Components
            </span>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {costFactors.map((factor, index) => {
            const IconComponent = factor.icon;
            return <motion.div key={factor.title} data-index={index} variants={fadeIn} className="flex flex-col justify-between border border-border bg-card p-5">
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex size-9 items-center justify-center border border-border bg-muted/60 text-foreground">
                        <IconComponent className="size-4" />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">
                        0{index + 1}
                      </span>
                    </div>
                    <h4 className="font-serif text-lg font-semibold uppercase tracking-wide text-foreground">
                      {factor.title}
                    </h4>
                    <p className="mt-2 text-sm leading-normal text-muted-foreground">
                      {factor.detail}
                    </p>
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>

        <Separator className="my-12" />

        {/* Action Callout */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={fadeIn} className="flex flex-col items-center justify-between gap-6 border border-border bg-muted/40 p-6 sm:flex-row sm:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden size-10 shrink-0 items-center justify-center border border-border bg-background text-primary sm:flex">
              <FileText className="size-5" />
            </div>
            <div>
              <h4 className="font-serif text-xl font-bold uppercase tracking-wide text-foreground">
                Detailed Mechanical Bull Pricing Breakdown
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Review complete turnkey package figures, used machine considerations, and freight guidelines.
              </p>
            </div>
          </div>

          <Button asChild size="lg" className="w-full shrink-0 border border-primary bg-primary font-medium tracking-wide text-primary-foreground hover:bg-primary/90 sm:w-auto">
            <Link to="/MechanicalBullPriceGuidePage">
              <span>READ MECHANICAL BULL PRICE GUIDE</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}